import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import SglContext from '../../SglContext';
import './associatedpeople.css';
import ReactModal from "react-modal";
import { LoadingSpinner } from '../teamManagement/utilities';

const AssociatedPeople = () => {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [masterIds, setMasterIds] = useState([]);
  const [searchType, setSearchType] = useState('name'); // 'name' or 'membership'
  const [searchData, setSearchData] = useState({
    firstName: '',
    lastName: '',
    membershipType: 'USEF', // 'usef', 'fei', 'ec'
    membershipNumber: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const Sgl = useContext(SglContext);

  // Fetch people data from backend
  const fetchPeople = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${Sgl.nestApiUrl}/associated-people/get-people`, {
        params: { customer_id: Sgl.company_id, }
      });
      setPeople(response.data);
      const ids = response.data.map(person => person.master_id);
      setMasterIds(ids);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch people data');
      console.error('Error fetching people:', err);
    } finally {
      setLoading(false);
    }
  };

  // Remove person from backend and update state
  const handleRemovePerson = async (master_id, people_id, customer_id) => {
    try {
      setError(null);
      await axios.delete(`${Sgl.nestApiUrl}/associated-people/remove-people`, {
        params: {
          master_id,
          people_id,
          customer_id,
        },
      });
      
      // If successful, refresh the list
      fetchPeople();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove person');
      console.error('Error removing person:', err);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchPeople();
  }, []);

  // Retry function for error state
  const handleRetry = () => {
    fetchPeople();
  };

  // Generate initials from first and last name
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || '??';
  };

  // Render ID numbers
  const renderIds = (person) => {
    const ids = [];
    if (person.usef_number) ids.push(`USEF: ${person.usef_number}`);
    if (person.fei_id) ids.push(`FEI: ${person.fei_id}`);
    if (person.ec_sln) ids.push(`EC: ${person.ec_sln}`);
    
    return ids.map((id, index) => (
      <span key={index} className="id-item">
        <span className="id-label">{id.split(':')[0]}:</span> {id.split(':')[1]}
      </span>
    ));
  };

  // Open search dialog
  const handleOpenSearch = () => {
    setShowSearchDialog(true);
    setSearchResults([]);
    setSearchError(null);
    setHasSearched(false);
    setSearchData({
      firstName: '',
      lastName: '',
      membershipType: 'USEF',
      membershipNumber: ''
    });
  };

  // Close search dialog
  const handleCloseSearch = () => {
    setShowSearchDialog(false);
    setHasSearched(false);
    setSearchResults([]);
    setSearchError(null);
    fetchPeople();
  };

  // Handle search form submission
  const handleSearchSubmit = async () => {
    setSearchResults([]);
    setHasSearched(true);
    try {
      setSearchLoading(true);
      setSearchError(null);
      
      let searchParams = {};
      if (searchType === 'name') {
        searchParams = {
          firstName: searchData.firstName,
          lastName: searchData.lastName
        };
      } else {
        searchParams = {
          membershipType: searchData.membershipType,
          membershipNumber: searchData.membershipNumber
        };
      }
      
      const response = await axios.get(`${Sgl.nestApiUrl}/associated-people/search`, {
        params: {
          searchType: searchType,
          firstName: searchParams.firstName,
          lastName: searchParams.lastName,
          membershipType: searchParams.membershipType,
          membershipNumber: searchParams.membershipNumber,
        },
      });
      
      setSearchResults(response.data);
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Failed to search people');
      console.error('Error searching people:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Add person from search results
  const handleAddPerson = async (personData) => {
    try {
      setSearchError(null);
      const response = await axios.post(`${Sgl.nestApiUrl}/associated-people/add-people`, {}, {
        params: {
          master_id: personData.master_id,
          membership_type: personData.membershipType,
          membership_number: personData.membershipNumber,
        },
      });
      // masterIds.push(personData.master_id);
      setMasterIds([...masterIds, personData.master_id]);
      // Refresh the main list
      // fetchPeople();
      // Close dialog
      // handleCloseSearch();
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Failed to add person');
      console.error('Error adding person:', err);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="associated-people-container">
        <div className="associated-people-header">
          <h3 className="associated-people-title">Associated People</h3>
        </div>
        <div className="loading-container">
          <div className="loading-text">Loading people data...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="associated-people-container">
        <div className="associated-people-header">
          <h3 className="associated-people-title">Associated People</h3>
        </div>
        <div className="error-container">
          <div className="error-message">Error: {error}</div>
          <button className="retry-btn" onClick={handleRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="associated-people-container">
      <div className="associated-people-header">
        <div className="title-with-refresh">
          <h3 className="associated-people-title">Associated People</h3>
          <div 
            className="associated-people-refresh-icon"
            onClick={fetchPeople}
            title="Refresh list"
          >
            <img 
              src="data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23000'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'%3e%3c/path%3e%3c/svg%3e"
              alt="Refresh"
            />
          </div>
        </div>
        <div className="controls">
          <button 
            className="associated-people-search-btn"
            onClick={handleOpenSearch}
          >
            Search
          </button>
        </div>
      </div>

      {people.length === 0 ? (
        <div className="empty-state">
          {people.length === 0 ? 'No associated people found!' : 'No people match your search!'}
        </div>
      ) : (
        <div className="contacts-list">
          {people.map((person) => (
            <div key={`${person.master_id}-${person.people_id}-${person.customer_id}`} className="contact-item">
              <div className="contact-avatar">
                {person.first_name || person.last_name ? (
                  getInitials(person.first_name, person.last_name)
                ) : (
                  getInitials(person.company_name, '')
                )}
              </div>
              
              <div className="contact-info">
                <div className="contact-name">
                  {person.first_name || person.last_name ? (
                    <div className="result-name">
                      {person.first_name} {person.last_name}
                    </div>
                  ) : (
                    <div className="result-name">
                      {person.company_name}
                    </div>
                  )}
                </div>
                
                {(person.usef_number || person.fei_id || person.ec_sln) && (
                  <div className="contact-details">
                    {(person.usef_number || person.fei_id || person.ec_sln) && (
                      <div className="contact-ids">
                        {renderIds(person)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="contact-actions">
                {person.master_id > 0 && (
                  <div className="verified-badge">
                    <div className="verified-icon">✓</div>
                    <span>Verified</span>
                  </div>
                )}
                
                <button
                  className="delete-btn"
                  onClick={() => handleRemovePerson(person.master_id, person.people_id, person.customer_id)}
                  title="Remove person"
                >
                <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ReactModal
        isOpen={showSearchDialog}
        onRequestClose={handleCloseSearch}
        className="modern-team-modal search-dialog-wide"
        overlayClassName="modern-team-overlay"
        ariaHideApp={false}  // prevents accessibility warning in dev
      >
        <div className="modern-modal-header">
          <h2 className="search-people-modal-title">Search People</h2>
          <button className="modern-close-btn" onClick={handleCloseSearch}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="modern-modal-body">
          {/* Search Type Selection */}
          <div className="search-type-tabs">
            <div className="tab-group">
              <div 
                className={`tab-button ${searchType === "name" ? "active" : ""}`}
                onClick={() => { 
                  setSearchType("name");
                  setHasSearched(false);
                  setSearchResults([]);
                  setSearchData({ firstName: '', lastName: ''})
                }}
              >
                Search by Name
              </div>
              <div 
                className={`tab-button ${searchType === "membership" ? "active" : ""}`}
                onClick={() => { 
                  setSearchType("membership");
                  setHasSearched(false);
                  setSearchResults([]);
                  setSearchData({ membershipType: 'USEF', membershipNumber: ''});
                }}
              >
                Search by Membership
              </div>
            </div>
          </div>

          {/* Search Form Fields */}
          {searchType === "name" ? (
            <>
              <div className="modern-form-group">
                <label>First Name</label>
                <input
                  type="text"
                  placeholder="Enter first name"
                  value={searchData.firstName}
                  onChange={(e) =>
                    setSearchData({ ...searchData, firstName: e.target.value })
                  }
                  className="modern-input"
                />
              </div>
              <div className="modern-form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  placeholder="Enter last name"
                  value={searchData.lastName}
                  onChange={(e) =>
                    setSearchData({ ...searchData, lastName: e.target.value })
                  }
                  className="modern-input"
                />
              </div>
            </>
          ) : (
            <>
              <div className="modern-form-group">
                <label>Membership Type</label>
                <select
                  value={searchData.membershipType}
                  onChange={(e) =>
                    setSearchData({ ...searchData, membershipType: e.target.value })
                  }
                  className="search-membership-select"
                >
                  <option value="USEF">USEF</option>
                  <option value="FEI">FEI</option>
                  <option value="EC">EC</option>
                </select>
              </div>
              <div className="modern-form-group">
                <label>Membership Number</label>
                <input
                  type="text"
                  placeholder="Enter membership number"
                  value={searchData.membershipNumber}
                  onChange={(e) =>
                    setSearchData({
                      ...searchData,
                      membershipNumber: e.target.value,
                    })
                  }
                  className="modern-input"
                />
              </div>
            </>
          )}

          {/* Search Results */}
          {searchError && (
            <div>
              <strong>Error:</strong> {searchError}
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="search-results">
              <div className="results-header-updated">
                <span className="results-title">Search Results</span>
                <span className="results-count-badge">{searchResults.length}</span>
              </div>
              <div className="results-list">
                {searchResults.map((person) => (
                  <div key={person.master_id} className="result-item-updated">
                    <div className="result-info">
                      {person.first_name || person.last_name ? (
                        <div className="result-name">
                          {person.first_name} {person.last_name}
                        </div>
                      ) : (
                        <div className="result-name">
                          {person.company_name}
                        </div>
                      )}
                      
                      {(person.usef_number || person.fei_id || person.ec_sln) && (
                        <div className="contact-details">
                          {(person.usef_number || person.fei_id || person.ec_sln) && (
                            <div className="contact-ids">
                              {renderIds(person)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {masterIds.includes(person.master_id) ? (
                      <div className="status-added-updated">
                        <span className="status-icon-added">✓</span>
                        <span>Added</span>
                      </div>
                    ) : (
                      <button className="add-btn-updated" onClick={() => handleAddPerson(person)} title="Add person">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <line x1="12" y1="5" x2="12" y2="19"/>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        <span>Add</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {searchResults.length === 0 &&
            !searchLoading &&
            !searchError &&
            hasSearched && (
              <div className="no-results">
                <div>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="11"
                      cy="11"
                      r="8"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M21 21l-4.35-4.35"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div>
                  <h4>No results found</h4>
                  <p>Try adjusting your search criteria</p>
                </div>
              </div>
            )}
        </div>

        <div className="modern-modal-footer modal-footer-normal">
          <button className="modern-btn-cancel" onClick={handleCloseSearch}>
            Cancel
          </button>
          <button
            className="modern-btn-primary"
            onClick={handleSearchSubmit}
            disabled={searchLoading || (searchType === 'name' && !searchData.firstName && !searchData.lastName) || (searchType === 'membership' && !searchData.membershipNumber)}
          >
            {searchLoading ? <LoadingSpinner message="" /> : "Search"}
          </button>
        </div>
      </ReactModal>

    </div>
  );
};

export default AssociatedPeople;